import {REGISTERED_AXIS_WGHT} from '@packages/systems/core/constants/Fonts';
import {
  ActionItemTargetType,
  MoveActionType,
  ScaleActionType,
  RotateActionType,
  SkewActionType,
  OpacityActionType,
  FilterActionType,
  FontVariationActionType,
  BackgroundColorActionType,
  BorderColorActionType,
  TextColorActionType,
  SizeActionType,
  HideShowActionType,
  PluginLottieActionType,
  PluginSplineActionType,
  PluginRiveActionType,
  PluginVariableActionType,
  FontVariationItemConfigType,
  // Shared
  ActionId,
} from '@packages/systems/ix2/types-core';

import {
  ActionTypeConsts,
  ActionAppliesTo,
} from '@packages/systems/ix2/shared-constants';

const {
  TRANSFORM_MOVE,
  TRANSFORM_SCALE,
  TRANSFORM_ROTATE,
  TRANSFORM_SKEW,
  STYLE_OPACITY,
  STYLE_SIZE,
  STYLE_FILTER,
  STYLE_FONT_VARIATION,
  STYLE_BACKGROUND_COLOR,
  STYLE_BORDER,
  STYLE_TEXT_COLOR,
  PLUGIN_LOTTIE,
  PLUGIN_SPLINE,
  PLUGIN_RIVE,
  PLUGIN_VARIABLE,
  GENERAL_DISPLAY,
} = ActionTypeConsts;

/**
 * Action Records
 */

export const defaultActionConfigTarget: ActionItemTargetType = {
  pluginElement: undefined,
  objectId: undefined,
  nodeId: null,
  appliesTo: ActionAppliesTo.ELEMENT,
  styleBlockIds: [],
  useEventTarget: undefined,
  boundaryMode: false,
};

const defaultActionId = '' as ActionId;

export const defaultMoveAction: MoveActionType = {
  id: defaultActionId,
  actionTypeId: TRANSFORM_MOVE,
  config: {
    delay: 0,
    easing: '',
    duration: 500,
    target: defaultActionConfigTarget,
    xValue: undefined,
    yValue: undefined,
    zValue: undefined,
    xUnit: 'PX',
    yUnit: 'PX',
    zUnit: 'PX',
  },
  instant: false,
};

export const defaultScaleAction: ScaleActionType = {
  id: defaultActionId,
  actionTypeId: TRANSFORM_SCALE,
  config: {
    delay: 0,
    easing: '',
    duration: 500,
    target: defaultActionConfigTarget,
    xValue: undefined,
    yValue: undefined,
    zValue: undefined,
    locked: false,
  },
  instant: false,
};

export const defaultRotateAction: RotateActionType = {
  id: defaultActionId,
  actionTypeId: TRANSFORM_ROTATE,
  config: {
    delay: 0,
    easing: '',
    duration: 500,
    target: defaultActionConfigTarget,
    xValue: undefined,
    yValue: undefined,
    zValue: undefined,
    xUnit: 'DEG',
    yUnit: 'DEG',
    zUnit: 'DEG',
  },
  instant: false,
};

export const defaultSkewAction: SkewActionType = {
  id: defaultActionId,
  actionTypeId: TRANSFORM_SKEW,
  config: {
    delay: 0,
    easing: '',
    duration: 500,
    target: defaultActionConfigTarget,
    xValue: undefined,
    yValue: undefined,
    zValue: undefined,
    xUnit: 'DEG',
    yUnit: 'DEG',
    zUnit: 'DEG', // interestingly there is no z skew — we should be able to remove this
  },
  instant: false,
};

export const defaultOpacityAction: OpacityActionType = {
  id: defaultActionId,
  actionTypeId: STYLE_OPACITY,
  config: {
    delay: 0,
    easing: '',
    duration: 500,
    target: defaultActionConfigTarget,
    value: 100,
    unit: '%',
  },
  instant: false,
};

export const defaultFilterAction: FilterActionType = {
  id: defaultActionId,
  actionTypeId: STYLE_FILTER,
  config: {
    delay: 0,
    easing: '',
    duration: 500,
    target: defaultActionConfigTarget,
    filters: [],
  },
  instant: false,
};

export const defaultFontVariationAction: FontVariationActionType = {
  id: defaultActionId,
  actionTypeId: STYLE_FONT_VARIATION,
  config: {
    delay: 0,
    easing: '',
    duration: 500,
    target: defaultActionConfigTarget,
    fontVariations: [],
  },
  instant: false,
};

export const defaultBackgroundColorAction: BackgroundColorActionType = {
  id: defaultActionId,
  actionTypeId: STYLE_BACKGROUND_COLOR,
  config: {
    delay: 0,
    easing: '',
    duration: 500,
    target: defaultActionConfigTarget,
    globalSwatchId: '',
    rValue: undefined,
    bValue: undefined,
    gValue: undefined,
    aValue: undefined,
  },
  instant: false,
};

export const defaultBorderColorAction: BorderColorActionType = {
  id: defaultActionId,
  actionTypeId: STYLE_BORDER,
  config: {
    delay: 0,
    easing: '',
    duration: 500,
    target: defaultActionConfigTarget,
    globalSwatchId: '',
    rValue: undefined,
    bValue: undefined,
    gValue: undefined,
    aValue: undefined,
  },
  instant: false,
};

export const defaultTextColorAction: TextColorActionType = {
  id: defaultActionId,
  actionTypeId: STYLE_TEXT_COLOR,
  config: {
    delay: 0,
    easing: '',
    duration: 500,
    target: defaultActionConfigTarget,
    globalSwatchId: '',
    rValue: undefined,
    bValue: undefined,
    gValue: undefined,
    aValue: undefined,
  },
  instant: false,
};

export const defaultSizeAction: SizeActionType = {
  id: defaultActionId,
  actionTypeId: STYLE_SIZE,
  config: {
    delay: 0,
    easing: '',
    duration: 500,
    target: defaultActionConfigTarget,
    widthValue: undefined,
    heightValue: undefined,
    widthUnit: 'PX',
    heightUnit: 'PX',
    locked: false,
  },
  instant: false,
};

export const defaultHideShowAction: HideShowActionType = {
  id: defaultActionId,
  actionTypeId: GENERAL_DISPLAY,
  config: {
    delay: 0,
    easing: '',
    duration: 500,
    target: defaultActionConfigTarget,
    value: undefined,
  },
  instant: true,
};

export const defaultPluginLottieAction: PluginLottieActionType = {
  id: defaultActionId,
  actionTypeId: PLUGIN_LOTTIE,
  config: {
    delay: 0,
    easing: '',
    duration: 500,
    target: defaultActionConfigTarget,
    value: 0,
  },
  instant: false,
};

export const defaultPluginSplineAction: PluginSplineActionType = {
  id: defaultActionId,
  actionTypeId: PLUGIN_SPLINE,
  config: {
    delay: 0,
    easing: '',
    duration: 500,
    target: defaultActionConfigTarget,
    value: {
      positionX: undefined,
      positionY: undefined,
      positionZ: undefined,
      rotationX: undefined,
      rotationY: undefined,
      rotationZ: undefined,
      scaleX: undefined,
      scaleY: undefined,
      scaleZ: undefined,
    },
  },
  instant: false,
};

export const defaultPluginRiveAction: PluginRiveActionType = {
  id: defaultActionId,
  actionTypeId: PLUGIN_RIVE,
  config: {
    delay: 0,
    easing: '',
    duration: 500,
    target: defaultActionConfigTarget,
    value: {
      name: undefined,
      inputs: undefined,
    },
  },
  instant: false,
};

export const defaultPluginVariableAction: PluginVariableActionType = {
  id: defaultActionId,
  actionTypeId: PLUGIN_VARIABLE,
  config: {
    delay: 0,
    easing: '',
    duration: 500,
    target: defaultActionConfigTarget,
    value: {
      red: undefined,
      green: undefined,
      blue: undefined,
      alpha: undefined,
      size: undefined,
      unit: undefined,
    },
  },
  instant: false,
};

/**
 * Action item config Records
 */

export const defaultFilterItemConfig = {
  type: 'blur',
  filterId: null,
  value: 5,
  unit: 'px',
} as const;

export const defaultFontVariationItemConfig: FontVariationItemConfigType = {
  type: REGISTERED_AXIS_WGHT,
  id: null,
  value: 1,
};
